/**
 * RP+TP+FC 组合血缘转出 — §8.1 (至少两种积分).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedTextarea,
} from '../components/ui/ThemedSurface';
import { BUYFUEL_COLORS } from '@/constants';

function fmtNum(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export function LineageCombinedTransferPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);

  const [toUsername, setToUsername] = useState('');
  const [amountRp, setAmountRp] = useState('');
  const [amountTp, setAmountTp] = useState('');
  const [amountFc, setAmountFc] = useState('');
  const [memo, setMemo] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: bal } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances(),
  });

  const trimmedUser = toUsername.trim();
  const previewQuery = useQuery({
    queryKey: ['lineage-transfer', 'preview', trimmedUser],
    queryFn: () => usersAPI.lineageTransferPreview({ to_username: trimmedUser }),
    enabled: trimmedUser.length >= 2,
  });

  const nickname =
    (previewQuery.data as { recipient?: { nickname?: string } } | undefined)?.recipient?.nickname?.trim() ??
    '';

  const securityQuestion =
    ((profile as Record<string, unknown> | null)?.security_question as string | undefined) ??
    t('common.defaultSecurityQuestion');

  const submitMut = useMutation({
    mutationFn: () =>
      usersAPI.lineageTransfer({
        to_username: trimmedUser,
        amount_rp: amountRp.trim() || undefined,
        amount_tp: amountTp.trim() || undefined,
        amount_fc: amountFc.trim() || undefined,
        transaction_password: txPassword,
        security_answer: securityAnswer,
        memo: memo.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user', 'balances'] });
      setError(null);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('lineageTransfer.failed', '转出失败'));
    },
  });

  function positiveCount() {
    return [amountRp, amountTp, amountFc].filter((v) => Number(v) > 0).length;
  }

  function handleSubmit() {
    setError(null);
    if (!trimmedUser) {
      setError(t('lineageTransfer.recipientRequired', '请输入接收账户'));
      return;
    }
    if (positiveCount() < 2) {
      setError(t('lineageTransfer.combinedMinAssets', '至少填写两种积分转出数量'));
      return;
    }
    if (!txPassword.trim() || !securityAnswer.trim()) {
      setError(t('common.enterTxPassword'));
      return;
    }
    submitMut.mutate();
  }

  useEffect(() => {
    if (!submitMut.isSuccess) return;
    const timer = window.setTimeout(() => navigate(-1), 1200);
    return () => window.clearTimeout(timer);
  }, [submitMut.isSuccess, navigate]);

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t('lineageTransfer.combinedTitle', 'RP+TP+FC')}
          onBack={() => navigate(-1)}
          theme={C}
        />

        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px] pb-28">
          <ThemedPanel className="grid grid-cols-3 gap-2 p-4 text-center text-xs" theme={C}>
            <div>
              <div style={{ color: C.muted }}>TP</div>
              <div className="mt-1 font-mono font-bold" style={{ color: C.gold }}>
                {fmtNum(bal?.balances?.tp ?? '0')}
              </div>
            </div>
            <div>
              <div style={{ color: C.muted }}>RP</div>
              <div className="mt-1 font-mono font-bold" style={{ color: C.gold }}>
                {fmtNum(bal?.balances?.rp ?? '0')}
              </div>
            </div>
            <div>
              <div style={{ color: C.muted }}>FC</div>
              <div className="mt-1 font-mono font-bold" style={{ color: C.gold }}>
                {fmtNum(bal?.balances?.fc ?? '0')}
              </div>
            </div>
          </ThemedPanel>

          <ThemedNotice tone="warning">
            <div className="mb-1 text-xs font-semibold">#提示#</div>
            <p className="text-xs leading-relaxed">
              {t(
                'lineageTransfer.combinedRule',
                '向有直接血缘关系的上下级转出，至少两种积分须同时填写；RP 仅可下转；上转 FC 须双方 A3+',
              )}
            </p>
          </ThemedNotice>

          <ThemedPanel className="space-y-4 p-4" theme={C}>
            <ThemedField label="TP">
              <ThemedInput type="number" value={amountTp} onChange={(e) => setAmountTp(e.target.value)} />
            </ThemedField>
            <ThemedField label="RP">
              <ThemedInput type="number" value={amountRp} onChange={(e) => setAmountRp(e.target.value)} />
            </ThemedField>
            <ThemedField label="FC">
              <ThemedInput type="number" value={amountFc} onChange={(e) => setAmountFc(e.target.value)} />
            </ThemedField>

            <ThemedField label={t('lineageTransfer.recipient', '接收账户')} required>
              <ThemedInput
                value={toUsername}
                onChange={(e) => setToUsername(e.target.value)}
                placeholder={t('lineageTransfer.recipientPlaceholder', '请输入接收玩家用户名')}
              />
            </ThemedField>

            <ThemedField label={t('lineageTransfer.nickname', '玩家昵称')}>
              <ThemedInput readOnly value={nickname} placeholder={t('lineageTransfer.nicknameHint', '输入用户名会自动显示')} />
            </ThemedField>

            <TransactionSecurity
              txPassword={txPassword}
              onTxPasswordChange={setTxPassword}
              showSecurity={showSecurity}
              onShowSecurityChange={setShowSecurity}
              securityAnswer={securityAnswer}
              onSecurityAnswerChange={setSecurityAnswer}
              securityQuestionText={securityQuestion}
              disabled={submitMut.isPending}
            />

            <ThemedField label={t('lineageTransfer.memo', '备注信息')}>
              <ThemedTextarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={3} maxLength={500} />
            </ThemedField>
          </ThemedPanel>

          {error ? <ThemedNotice tone="error">{error}</ThemedNotice> : null}
          {submitMut.isSuccess ? <ThemedNotice tone="success">{t('lineageTransfer.success', '转出成功')}</ThemedNotice> : null}
        </div>

        <ThemedBottomActionBar background={BUYFUEL_COLORS.bottomBarBg} className="pb-5 pt-3">
          <button
            type="button"
            disabled={submitMut.isPending || submitMut.isSuccess}
            onClick={handleSubmit}
            className="h-11 w-full rounded-xl border text-sm font-semibold disabled:opacity-50"
            style={{ background: C.buttonBg, borderColor: C.buttonBorder, color: C.text }}
          >
            {t('lineageTransfer.confirm', '确定转出')}
          </button>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}
